import { useMemo } from 'react';
import { SearchableSelect } from './searchable-select';
import { useDoctors } from '../../services/httpServices/queries';
import { AlertCircle, Stethoscope } from 'lucide-react';

interface DoctorSearchSelectProps {
  value: string;
  onChange: (doctorId: string, doctor?: any) => void;
  label?: React.ReactNode;
  required?: boolean;
  error?: React.ReactNode;
  disabled?: boolean;
  placeholder?: string;
  /** Hide the selected-doctor preview card when space is tight. */
  showPreview?: boolean;
  /** Opens a quick-create flow; receives the text typed into the search box. */
  onAddNew?: (query: string) => void;
  addNewLabel?: string;
  /** Label kept visible when the selected value is not in the active list. */
  selectedLabel?: string;
}

function doctorName(doctor: any): string {
  return (
    doctor?.name ||
    `${doctor?.firstName || ''} ${doctor?.lastName || ''}`.trim() ||
    doctor?.employeeCode ||
    'Doctor'
  );
}

/**
 * Searchable dropdown of active doctors. Mirrors `EmployeeSearchSelect` so a
 * "Doctor" referral partner is picked the same way an employee is — options show
 * the doctor's name plus code / designation / department. Selecting a value also
 * returns the full doctor so callers can prefill data.
 */
export function DoctorSearchSelect({
  value,
  onChange,
  label,
  required = false,
  error,
  disabled = false,
  placeholder = 'Search doctor by name or code…',
  showPreview = true,
  onAddNew,
  addNewLabel,
  selectedLabel,
}: DoctorSearchSelectProps) {
  const { data, isLoading } = useDoctors(1, 500, { status: 'active' });
  const doctors = useMemo(() => data?.data ?? [], [data]);

  const options = useMemo(() => {
    const base = doctors.map((doctor: any) => ({
      value: doctor.id,
      label: doctorName(doctor),
      description: [doctor.employeeCode, doctor.designation?.name, doctor.department?.name]
        .filter(Boolean)
        .join(' · '),
      keywords: [doctor.employeeCode, doctor.email, doctor.phone].filter(Boolean) as string[],
    }));
    // Keep the current selection visible even if it is not in the active list
    // (e.g. a just-created doctor, or one that was deactivated afterwards).
    if (value && !base.some((o) => o.value === value)) {
      base.unshift({ value, label: selectedLabel || 'Selected doctor', description: undefined as any, keywords: [] });
    }
    return base;
  }, [doctors, value, selectedLabel]);

  const selected = useMemo(
    () => doctors.find((doctor: any) => doctor.id === value),
    [doctors, value],
  );

  return (
    <div>
      <SearchableSelect
        value={value}
        onChange={(id) => onChange(id, doctors.find((doctor: any) => doctor.id === id))}
        options={options}
        placeholder={placeholder}
        loading={isLoading}
        disabled={disabled}
        onAddNew={onAddNew}
        addNewLabel={addNewLabel}
        label={
          label !== undefined ? (
            <>
              {label}
              {required && <span className="text-danger"> *</span>}
            </>
          ) : undefined
        }
        labelClassName="block text-sm font-medium text-text mb-1"
        error={
          error ? (
            <p className="flex items-center gap-1 mt-1 text-xs text-danger">
              <AlertCircle size={12} />
              {error}
            </p>
          ) : undefined
        }
      />

      {showPreview && selected && (
        <div className="mt-2 flex items-center gap-3 p-2.5 rounded-lg border border-primary/20 bg-primary/5">
          <div className="w-9 h-9 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center overflow-hidden">
            {selected.photoUrl ? (
              <img src={selected.photoUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <Stethoscope size={16} />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-text truncate">{doctorName(selected)}</p>
            <p className="text-xs text-text-muted truncate">
              {[selected.employeeCode, selected.designation?.name, selected.department?.name]
                .filter(Boolean)
                .join(' · ') || 'Doctor'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
